import { useState } from 'react';
import { Alert, Button, Card, Center, PinInput, Stack, Text, TextInput } from '@mantine/core';
import type { Actor } from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';

type LoginResult =
  | { status: 'ok'; accessToken: string; actor: Actor }
  | { status: 'mfa_enrollment_required' }
  | { status: 'org_selection_required'; orgs: { orgId: string; roleKey: string }[] };

/**
 * Вход в портал. Второй фактор обязателен: менеджер по безопасности видит
 * весь флот, и это лакомая цель.
 *
 * Строки интерфейса английские: продукт для флотов США. Комментарии остаются
 * русскими — это заметки разработчику, а не текст на экране.
 */
export function Login(): React.JSX.Element {
  const setSession = useSession((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<LoginResult>('/auth/login', {
        method: 'POST',
        body: { email, password, ...(totp.length === 6 ? { totp } : {}) },
      });

      if (result.status === 'ok') {
        setSession(result.accessToken, result.actor);
        return;
      }
      setError(
        result.status === 'mfa_enrollment_required'
          ? 'Two-factor authentication setup required'
          : 'Select an organization',
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Wrong email, password, or code'
          : 'Could not sign in',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Center mih="100dvh" p="md">
      <Card w={396} shadow="sm" padding="lg" radius="md">
        <Stack gap="md">
          <div>
            <Text fw={680} fz="lg" style={{ letterSpacing: '-0.02em' }}>
              ProofFleet
            </Text>
            <Text c="dimmed" fz="sm">
              Safety portal
            </Text>
          </div>

          <TextInput
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <TextInput
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />

          <div>
            <Text fw={500} fz="sm" mb={6}>
              Authenticator code
            </Text>
            <PinInput length={6} type="number" inputMode="numeric" value={totp} onChange={setTotp} />
          </div>

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button
            fullWidth
            loading={busy}
            disabled={email === '' || password === ''}
            onClick={() => void submit()}
          >
            Sign in
          </Button>
        </Stack>
      </Card>
    </Center>
  );
}
